import { CommonCalendarManager, genUrlParams, CalendarApiParams } from '@benzinga/calendar-commons';
import { SafePromise } from '@benzinga/safe-await';
import { Session } from '@benzinga/session';
import { Dividend, DividendsQueryParams } from './entities';
import { DividendsCalendarEnvironment } from './environment';

const dividendsUrlParams = (params: DividendsQueryParams): CalendarApiParams => {
  const baseParams = genUrlParams(params);
  const { dateSort, dividendYield, dividendYieldOperation } = params;

  return {
    ...baseParams,
    parameters: {
      ...baseParams.parameters,
      ...(dateSort && { date_sort: dateSort }),
      ...(dividendYield !== undefined && { dividend_yield: dividendYield }),
      ...(dividendYieldOperation && { dividend_yield_operation: dividendYieldOperation }),
    },
  };
};

/**
 * Calendar dividends manager
 *
 * To use, obtain an instance by calling session.getManager(DividendsCalendarManager)
 *
 * @export
 * @class DividendsCalendarManager
 * @extends {CommonCalendarManager<'dividends', Dividend, DividendsQueryParams>}
 */
export class DividendsCalendarManager extends CommonCalendarManager<'dividends', Dividend, DividendsQueryParams> {
  constructor(session: Session) {
    super(session, session.getEnvironment(DividendsCalendarEnvironment), 'dividends', {
      formatResponse: (data: unknown): Dividend[] => {
        if (Array.isArray(data)) return data;
        return (data as Record<string, Dividend[] | undefined>)?.dividends ?? [];
      },
      genUrlParams: dividendsUrlParams,
    });
  }

  /**
   * @internal
   *
   * @static
   * @memberof DividendsCalendarManager
   */
  public static getName = () => 'benzinga-calendar-dividends';

  /**
   * Fetch dividends calendar records, filtered by params
   *
   * @param {DividendsQueryParams} params
   * @return {*}  {SafePromise<Dividend[]>}
   * @memberof DividendsCalendarManager
   */
  public getDividends(params: DividendsQueryParams): SafePromise<Dividend[]> {
    return this.request.fetchCalendarData(params);
  }
}
